// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import { Paper, Switch, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import noop from 'lodash/noop'
import omit from 'lodash/omit'
import React from 'react'
import { Mutation } from 'react-apollo'
import DeleteButton from '../../../common/iconButtons/DeleteButton'
import EditButton from '../../../common/iconButtons/EditButton'
import { alphaSort } from '../../../common/utils'
import RestrictedView from '../../auth/RestrictedView'
import withUser from '../../auth/withUser'
import { i18n } from '../../index'
import {
  deleteThumbableService,
  fetchStationThumbableServices,
  fetchThumbableServices,
  updateThumbableService,
} from './thumbableServiceQueries'
import css from './thumbableServiceTable.css'
import type { ThumbableService } from './thumbableServiceTypes'
import type { User } from './types'

const updateCacheOnDelete = (query: any, fieldName: string, id: string, stationId?: string) => (cache: any) => {
  const variables = stationId
    ? {
        stationId,
      }
    : {}
  const data = cache.readQuery({
    query,
    variables,
  })
  cache.writeQuery({
    query,
    variables,
    data: {
      [fieldName]: data[fieldName].filter((ts) => ts.id !== id),
    },
  })
}

type ThumbableServiceRowProps = {
  data: ThumbableService
  stationId?: string
  user: User
  onEdit: (...args: Array<any>) => any
  onDelete: (...args: Array<any>) => any
}

const onIsEnabledToggle = (onMutate: (...args: Array<any>) => any, data: ThumbableService) => {
  onMutate({
    variables: { ...data, isEnabled: !data.isEnabled },
  })
}

const getTotalNumberOfEnabledServices = (data: Array<ThumbableService>): number => {
  let total = 0
  data.forEach((item: ThumbableService) => {
    if (item.isEnabled) {
      ++total
    }
  })
  return total
}

const ThumbableServiceRow = ({ data, onEdit, onDelete, user }: ThumbableServiceRowProps) => (
  <TableRow>
    <TableCell className={css.tableCell}>{data.name}</TableCell>

    <TableCell className={css.tableCell}>
      <Mutation mutation={updateThumbableService}>
        {(onMutate) => (
          <Switch
            checked={data.isEnabled}
            onChange={() => onIsEnabledToggle(onMutate, data)}
            value='isEnabled'
            color='primary'
          />
        )}
      </Mutation>
    </TableCell>
    <RestrictedView requiredPermission='manage_ownStation'>
      <TableCell className={css.actionsCell}>
        <EditButton onClick={() => (data.id ? onEdit(omit(data, ['__typename'])) : noop)} />
        <Mutation mutation={deleteThumbableService}>
          {(onMutate) => <DeleteButton onClick={() => (data.id ? onDelete(onMutate, data.id) : noop)} />}
        </Mutation>
      </TableCell>
    </RestrictedView>
  </TableRow>
)

type ThumbableServiceTableProps = {
  data: Array<ThumbableService>
  stationId?: string
  onEdit: (...args: Array<any>) => any
}

@withUser
class ThumbableServiceTable extends React.PureComponent<ThumbableServiceTableProps> {
  onDelete = (onMutate: (...args: Array<any>) => any, id: string) => {
    const { stationId } = this.props
    const query = stationId ? fetchStationThumbableServices : fetchThumbableServices
    const fieldName = stationId ? 'stationThumbsUpItems' : 'thumbsUpItems'
    onMutate({
      variables: {
        id,
      },
      update: updateCacheOnDelete(query, fieldName, id, stationId),
    })
  }

  render() {
    const { data, stationId, onEdit, user } = this.props
    return (
      <React.Fragment>
        <h2>{i18n.t('admin.thumbableServices.pageTitle')}</h2>
        <Paper>
          <div className={css.component}>
            {data.length ? (
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell className={css.tableHeading}>{i18n.t('admin.incentiveProducts.label.name')}</TableCell>
                    <TableCell className={css.tableHeading} padding='checkbox'>
                      {i18n.t('admin.incentiveProducts.label.isEnabledTotal', {
                        total: getTotalNumberOfEnabledServices(data),
                      })}
                    </TableCell>
                    <TableCell className={css.tableHeading} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {alphaSort('name', data).map((ts) => (
                    <ThumbableServiceRow
                      data={ts}
                      stationId={stationId}
                      onEdit={onEdit}
                      onDelete={this.onDelete}
                      key={ts.id}
                      user={user}
                    />
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className={css.empty}>{i18n.t('admin.thumbableServices.noThumbableServices')}</p>
            )}
          </div>
        </Paper>
      </React.Fragment>
    )
  }
}

export default ThumbableServiceTable
